import { useMemo, useState } from "react";
import { userApi } from "../../apis/user/api";
import {
  FetchUserAccountsParams,
  FetchUserAccountsResponse,
} from "../../apis/user/types";
import { useUserSessionContext } from "../../contextApis/UserSessionContext";
import { RouteEnum, useRoute } from "../../contextApis/RouteContext";
import { LoadingTypesEnum } from "../../types/loading";
import { CompanyFormParams, PasswordFormParams } from "../../types/login";

export interface UserAccountsData {
  companyId: string;
  companyName: string;
  userName: string;
}

export interface Company {
  companyId: string;
  companyName: string;
}

const useLogIn = () => {
  const [step, setStep] = useState<"email" | "company" | "password">("email");
  const [error, setError] = useState<string>();

  const [loading, setLoading] = useState<LoadingTypesEnum>(
    LoadingTypesEnum.idle,
  );

  const [userAccountsData, setUserAccountsData] =
    useState<FetchUserAccountsResponse>([]);

  const [username, setUsername] = useState<string>("");

  const [company, setCompany] = useState<string>("");

  const { setSessionData } = useUserSessionContext();
  const { navigate } = useRoute();

  const companyOptions = useMemo((): Company[] => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return userAccountsData.map(({ userName, ...companyData }) => companyData);
  }, [userAccountsData]);

  const handleEmailSubmit = async (params: FetchUserAccountsParams) => {
    try {
      if (error) setError(undefined);

      setLoading(LoadingTypesEnum.pending);

      const response = await userApi.getUserAccounts(params);

      if (!response || response.length === 0) {
        setLoading(LoadingTypesEnum.fulfilled);

        setError("No account found for this email.");
        return;
      }

      setUserAccountsData(response);

      if (response.length === 1) {
        setUsername(response[0].userName);
        setCompany(response[0].companyId);
        setStep("password");
      } else {
        setStep("company");
      }

      setLoading(LoadingTypesEnum.fulfilled);
    } catch (err) {
      setLoading(LoadingTypesEnum.rejected);

      setError(
        "An error occurred while fetching your accounts. Please check that you typed your email correctly. If the problem persists, please contact support.",
      );
    }
  };

  const handleCompanySelect = (params: CompanyFormParams) => {
    setLoading(LoadingTypesEnum.pending);

    const selectedAccount = userAccountsData.find(
      (account) => account.companyId === params.company,
    );

    if (!selectedAccount) {
      setLoading(LoadingTypesEnum.rejected);
      setError("An error occurred while selecting your account.");
      return;
    }

    setUsername(selectedAccount.userName);
    setCompany(selectedAccount.companyId);
    setStep("password");
    setLoading(LoadingTypesEnum.fulfilled);
  };

  const handlePasswordSubmit = async (params: PasswordFormParams) => {
    if (!company || !username) {
      setError(
        "Missing company or username information. Please return to the previous step.",
      );
      return;
    }

    try {
      if (error) setError(undefined);

      setLoading(LoadingTypesEnum.pending);

      const loginResult = await userApi.signIn({
        tenant: company,
        username,
        password: params.password,
      });

      if (loginResult.isSuccess) {
        setSessionData(loginResult);

        navigate(RouteEnum.DRAYAGE_SEARCH);
      } else {
        setError("Wrong password");
      }

      setLoading(LoadingTypesEnum.fulfilled);
    } catch (err) {
      setLoading(LoadingTypesEnum.rejected);

      setError(
        "An error occurred during login. Please make sure your password is correct and try again. If the problem persists, please contact support.",
      );
    }
  };

  return {
    step,
    setStep,
    error,
    setError,
    isLoading: loading === LoadingTypesEnum.pending,
    companyOptions,
    handleEmailSubmit,
    handleCompanySelect,
    handlePasswordSubmit,
  };
};

export default useLogIn;
